// ============================================================================
// Authenticated surface resolver — Foundation B, stage 2
// ============================================================================
//
// This is deliberately a pure decision seam. It mirrors the App dispatch order
// closely enough to identify the surface that would mount, then permits a
// released render claim only when ALL of these independent facts hold:
//
//   1. a resolved authenticated identity and profile are present, and the
//      account is not inactive (migration 218);
//   2. the existing access contract allows that view to load;
//   3. the caller's session lease belongs to the current positive epoch;
//   4. the candidate is in the live authenticated manifest; and
//   5. the explicit boot-release ledger names that exact surface; and
//   6. the manifest records no remaining App-context dependency.
//
// Nothing in this module starts data loading or releases a surface by itself.
// Importing the manifest validates topology only; it never turns a data-owner
// declaration into a render claim.
// ============================================================================

import {APP_ROUTES_POST_FORM, APP_ROUTES_PRE_FORM} from '../appRoutes.js';
import {AUTHENTICATED_VIEWS, SURFACE_MANIFEST, VIEW_VARIANTS} from '../data/surfaceDataManifest.js';
import {routedLocationFor} from '../navigation/viewRoutes.js';
import {BROILER_ROUTES} from '../../broiler/broilerRoutes.js';
import {CATTLE_ROUTES} from '../../cattle/cattleRoutes.js';
import {LAYER_ROUTES} from '../../layer/layerRoutes.js';
import {PIG_ROUTES} from '../../pig/pigRoutes.js';
import {SHEEP_ROUTES} from '../../sheep/sheepRoutes.js';
import {isInactiveAccount, resolveSurfaceAccess, SURFACE_NAVIGATION} from '../../lib/access.js';
import {isProcessingRole, VIEW_TO_PROGRAM} from '../../lib/globalNav.js';
import {WEBFORMS_ROUTES} from '../../webforms/webformsRoutes.js';
import {BOOT_RELEASE_LEDGER, validateBootReleaseLedger} from './bootReleaseLedger.js';
import {
  BROILER_DAILYS_INDEX_PATHNAME,
  isCanonicalBroilerDailysDetailLocation,
  isCanonicalBroilerDailysIndexLocation,
} from './broilerDailysPaths.ts';
import {
  BROILER_BATCHES_INDEX_PATHNAME,
  isCanonicalBroilerBatchesDetailLocation,
  isCanonicalBroilerBatchesIndexLocation,
} from './broilerBatchesPaths.ts';
import {isCanonicalCattleBatchesDetailLocation} from './cattleBatchesDetailPath.ts';
import {CATTLE_BATCHES_INDEX_PATHNAME, isCanonicalCattleBatchesIndexLocation} from './cattleBatchesIndexPath.ts';
import {isCanonicalCattleDailysDetailPathname} from './cattleDailysDetailPath.ts';
import {isCanonicalCattleForecastBatchDetailLocation} from './cattleForecastBatchDetailPath.ts';
import {
  EGG_DAILYS_INDEX_PATHNAME,
  isCanonicalEggDailysDetailLocation,
  isCanonicalEggDailysIndexLocation,
} from './eggDailysPaths.ts';
import {isCanonicalEquipmentFuelingFormPathname} from './equipmentFuelingFormPath.ts';
import {
  CATTLE_HERDS_INDEX_PATHNAME,
  isCanonicalCattleHerdsDetailLocation,
  isCanonicalCattleHerdsIndexLocation,
} from './cattleHerdsPath.ts';
import {isCanonicalFleetIndexLocation} from './fleetIndexPath.ts';
import {isCanonicalFleetDetailPathname} from './fleetDetailPath.ts';
import {
  LAYER_DAILYS_INDEX_PATHNAME,
  isCanonicalLayerDailysDetailLocation,
  isCanonicalLayerDailysIndexLocation,
} from './layerDailysPaths.ts';
import {isCanonicalLayerBatchesDetailLocation} from './layerBatchesDetailPath.ts';
import {isCanonicalLayerHousingsDetailLocation} from './layerHousingsDetailPath.ts';
import {
  PIG_DAILYS_INDEX_PATHNAME,
  isCanonicalPigDailysDetailLocation,
  isCanonicalPigDailysIndexLocation,
} from './pigDailysPaths.ts';
import {
  PIG_BATCHES_INDEX_PATHNAME,
  isCanonicalPigBatchesDetailLocation,
  isCanonicalPigBatchesIndexLocation,
} from './pigBatchesPaths.ts';
import {
  isCanonicalSheepBatchesDetailLocation,
  isCanonicalSheepBatchesIndexLocation,
  isCanonicalSheepFlocksDetailLocation,
  isCanonicalSheepFlocksIndexLocation,
} from './sheepRouterPaths.ts';
import {
  SHEEP_DAILYS_INDEX_PATHNAME,
  isCanonicalSheepDailysDetailLocation,
  isCanonicalSheepDailysIndexLocation,
} from './sheepDailysPaths.ts';
import {isCanonicalTasksCenterLocation} from './tasksCenterPath.ts';
import {isCanonicalTasksInstanceDetailLocation, isCanonicalTasksTodoDetailLocation} from './tasksRecordPaths.ts';
import {isCanonicalFleetChecklistEntryPathname, isCanonicalFleetFuelingEntryPathname} from './fleetEntryPath.ts';
import {isCanonicalWeighInSessionRecordLocation} from './weighInSessionRecordPath.ts';
import {isCanonicalWebformHubDailysLocation} from './webformHubDailysPath.ts';
import {isCanonicalPigFarrowingLocation} from './pigFarrowingPath.ts';
import {isCanonicalPigSowsLocation} from './pigSowsPath.ts';
import type {BootReleaseLedger} from './bootReleaseLedger.js';
import type {AuthState as AccessAuthState} from '../../lib/access.js';
import type {RouteRow} from '../navigation/viewRoutes.js';

export const AUTHENTICATED_SURFACE_MODE = Object.freeze({
  LEGACY: 'legacy',
  RELEASED: 'released',
} as const);

export const AUTHENTICATED_SURFACE_FALLBACK = Object.freeze({
  MALFORMED_INPUT: 'malformed-input',
  UNKNOWN_VIEW: 'unknown-view',
  UNAUTHENTICATED: 'unauthenticated',
  PROFILE_UNRESOLVED: 'profile-unresolved',
  ACCESS_DENIED: 'access-denied',
  INVALID_SESSION_EPOCH: 'invalid-session-epoch',
  SESSION_SUBJECT_MISMATCH: 'session-subject-mismatch',
  ROUTE_UNRESOLVED: 'route-unresolved',
  UNKNOWN_SURFACE: 'unknown-surface',
  MALFORMED_RELEASE_LEDGER: 'malformed-release-ledger',
  UNRELEASED_SURFACE: 'unreleased-surface',
  RESIDUAL_LEGACY: 'residual-legacy',
} as const);

type AuthenticatedSurfaceFallback =
  (typeof AUTHENTICATED_SURFACE_FALLBACK)[keyof typeof AUTHENTICATED_SURFACE_FALLBACK];

export type AuthenticatedSurfaceResolverInput = Readonly<{
  /** The current App view state; unknown values are never routed optimistically. */
  view: unknown;
  /** A current react-router location or pathname; only pathname participates. */
  location: unknown;
  /** The existing full-screen broiler drawer state, if the caller has it. */
  showForm?: unknown;
  /** Non-empty when the broiler form is editing an existing batch; create mode on /broiler/timeline yields to view.timeline. */
  editId?: unknown;
  /** The current authenticated session generation. */
  sessionEpoch: unknown;
  /** The generation attached to the boot/resource lease attempting this load. */
  leaseEpoch: unknown;
  /**
   * Optional lease subject. Runtime boot boundaries provide it so a resolved
   * identity from an outgoing subject can never authorize a newly-opened
   * session epoch before React has committed its replacement auth state.
   */
  leaseUserId?: unknown;
  /** Omitted means the immutable production boot-release ledger. */
  ledger?: unknown;
  /** Current AuthContext identity. It is narrowed defensively at the boundary. */
  authState: unknown;
}>;

export type AuthenticatedSurfaceResolution =
  | Readonly<{
      mode: typeof AUTHENTICATED_SURFACE_MODE.RELEASED;
      reason: 'released';
      surface: string;
      routedPathname: string;
    }>
  | Readonly<{
      mode: typeof AUTHENTICATED_SURFACE_MODE.LEGACY;
      reason: AuthenticatedSurfaceFallback;
      /** Present only after a valid candidate was deliberately denied a released claim. */
      surface: string | null;
      routedPathname: string | null;
    }>;

type RoutedTable = readonly RouteRow[];

type ResolvedIdentity = Readonly<{
  accessAuthState: AccessAuthState;
  isLight: boolean;
  userId: string;
}>;

const authenticatedViews = new Set<string>(AUTHENTICATED_VIEWS);
const knownSurfaces = new Set<string>(Object.keys(SURFACE_MANIFEST));

// This is the existing post-gate dispatch order, excluding the two boundaries
// that interrupt it: APP_ROUTES_PRE_FORM before the full-screen form, and
// APP_ROUTES_POST_FORM after every program entry. Keeping it as data makes a
// future order change visible in the focused resolver proof.
const DOMAIN_DISPATCH_ORDER: readonly RoutedTable[] = Object.freeze([
  BROILER_ROUTES,
  PIG_ROUTES,
  CATTLE_ROUTES,
  SHEEP_ROUTES,
  LAYER_ROUTES,
]);

function own(object: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function validPathname(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\0') &&
    !value.includes('\r') &&
    !value.includes('\n')
  );
}

function pathnameFrom(location: unknown): string | null {
  if (validPathname(location)) return location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return null;
  const pathname = (location as {pathname?: unknown}).pathname;
  return validPathname(pathname) ? pathname : null;
}

/**
 * The released Layer Batches presenter is deliberately narrower than the
 * shared Layer router: only its bare canonical hub path owns the independent
 * boot. In particular `/layer/housings` is a router wildcard base whose
 * generic surface classification otherwise lands on the hub. Keep that raw
 * route out of the release grant before ledger membership, or the strict render
 * boundary would refuse the capability while App was left without an explicit
 * recovery disposition.
 */
function isCanonicalLayerBatchesIndexLocation(location: unknown): boolean {
  return pathnameFrom(location) === '/layer/batches';
}

function validEpoch(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function resolvedIdentity(value: unknown): ResolvedIdentity | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const auth = value as {user?: unknown; role?: unknown; profile?: unknown};
  if (!auth.user || typeof auth.user !== 'object' || Array.isArray(auth.user)) return null;
  const userId = (auth.user as {id?: unknown}).id;
  if (typeof userId !== 'string' || userId.trim() === '') return null;
  if (typeof auth.role !== 'string' || auth.role.trim() === '') return null;

  // Navigation tolerates an unresolved profile. A data release cannot: the
  // existing access contract documents that this would otherwise be fail-open.
  if (!auth.profile || typeof auth.profile !== 'object' || Array.isArray(auth.profile)) return null;

  return Object.freeze({
    accessAuthState: value as AccessAuthState,
    isLight: auth.role === 'light',
    userId,
  });
}

function routedPathnameFor(view: string, pathname: string, table: RoutedTable): string | null {
  const routed = routedLocationFor(view, {pathname}, table);
  if (routed === null) return null;
  return typeof routed === 'string' ? routed : validPathname(routed.pathname) ? routed.pathname : null;
}

function hasNonEmptySuffix(pathname: string, prefix: string): boolean {
  return pathname.startsWith(prefix) && pathname.slice(prefix.length).length > 0;
}

const CATTLE_DAILYS_INDEX_PATHNAME = '/cattle/dailys';

// The released Cattle Dailys index deliberately accepts presentation-only
// search/hash state, but no trailing-slash or descendant pathname. The generic
// router owns those wider shapes, so keep them out of the ledger grant before
// they can suppress explicit terminal recovery.
function isCanonicalCattleDailysIndexLocation(location: unknown): boolean {
  return pathnameFrom(location) === CATTLE_DAILYS_INDEX_PATHNAME;
}

/**
 * Map a routed view/path pair to the surface selected by the component that
 * actually mounts. This mirrors the current pathname branches, including
 * intentional oddities such as `/broiler/batches/` mounting the record page
 * while `/cattle/dailys/` falls back to its hub.
 */
function surfaceForRoutedView(view: string, pathname: string, isLight: boolean): string | null {
  if (!own(VIEW_VARIANTS, view)) return `view.${view}`;

  switch (view) {
    case 'home':
      return isLight ? 'app.home.light' : 'app.home.standard';
    case 'cattleherds':
      return hasNonEmptySuffix(pathname, '/cattle/herds/') ? 'cattle.herds.detail' : 'cattle.herds.index';
    case 'cattledailys':
      return hasNonEmptySuffix(pathname, '/cattle/dailys/') ? 'cattle.dailys.detail' : 'cattle.dailys.index';
    case 'cattlebatches':
      if (pathname.startsWith('/cattle/batches/forecast/')) return 'cattle.batches.forecastDetail';
      return hasNonEmptySuffix(pathname, '/cattle/batches/') ? 'cattle.batches.detail' : 'cattle.batches.index';
    case 'list':
      return pathname.startsWith('/broiler/batches/') ? 'broiler.batches.detail' : 'broiler.batches.index';
    case 'broilerdailys':
      return hasNonEmptySuffix(pathname, '/broiler/dailys/') ? 'broiler.dailys.detail' : 'broiler.dailys.index';
    case 'layerbatches':
      if (pathname.startsWith('/layer/housings/')) return 'layer.housings.detail';
      return pathname.startsWith('/layer/batches/') ? 'layer.batches.detail' : 'layer.batches.index';
    case 'layerdailys':
      return hasNonEmptySuffix(pathname, '/layer/dailys/') ? 'layer.dailys.detail' : 'layer.dailys.index';
    case 'eggdailys':
      return hasNonEmptySuffix(pathname, '/layer/eggs/') ? 'layer.eggDailys.detail' : 'layer.eggDailys.index';
    case 'pigdailys':
      return hasNonEmptySuffix(pathname, '/pig/dailys/') ? 'pig.dailys.detail' : 'pig.dailys.index';
    case 'pigbatches':
      return pathname.startsWith('/pig/batches/') ? 'pig.batches.detail' : 'pig.batches.index';
    case 'sheepdailys':
      return hasNonEmptySuffix(pathname, '/sheep/dailys/') ? 'sheep.dailys.detail' : 'sheep.dailys.index';
    case 'sheepbatches':
      return hasNonEmptySuffix(pathname, '/sheep/batches/') ? 'sheep.batches.detail' : 'sheep.batches.index';
    case 'sheepflocks':
      return hasNonEmptySuffix(pathname, '/sheep/flocks/') ? 'sheep.flocks.detail' : 'sheep.flocks.index';
    case 'tasks':
      if (pathname === '/tasks/todo' || pathname === '/tasks/todo/') return 'tasks.center';
      if (pathname.startsWith('/tasks/todo/')) return 'tasks.todo.detail';
      return hasNonEmptySuffix(pathname, '/tasks/') ? 'tasks.instance.detail' : 'tasks.center';
    case 'fuelingHub':
      if (pathname === '/equipment/supply') return 'equipment.fueling.supply';
      return pathname.startsWith('/equipment/') ? 'equipment.fueling.form' : 'equipment.fueling.hub';
    case 'equipmentHome':
      if (pathname.startsWith('/fleet/fueling/')) return 'equipment.fueling.entry';
      if (pathname.startsWith('/fleet/checklist/')) return 'equipment.checklist.entry';
      // EquipmentHome deliberately treats the Fuel Log as another mode of
      // the fleet surface: it consumes the same loadAll arrays and does not
      // mount a distinct data owner.
      if (pathname === '/fleet/fuel-log') return 'equipment.fleet.index';
      return pathname.startsWith('/fleet/') ? 'equipment.fleet.detail' : 'equipment.fleet.index';
    default:
      // A new manifest variant must add an explicit branch here. Returning null
      // is intentional: it denies a released claim rather than guessing a
      // parent or first declaration.
      return null;
  }
}

function resolveCandidate(
  view: string,
  pathname: string,
  showForm: boolean,
  editId: unknown,
  isLight: boolean,
): Readonly<{surface: string; routedPathname: string}> | null {
  // Signed-in webforms dispatch before App's own views and before the form.
  const webformsPath = routedPathnameFor(view, pathname, WEBFORMS_ROUTES);
  if (webformsPath !== null) {
    const surface = surfaceForRoutedView(view, webformsPath, isLight);
    return surface ? Object.freeze({surface, routedPathname: webformsPath}) : null;
  }

  // These five App views outrank the full-screen broiler form by contract.
  const appPreFormPath = routedPathnameFor(view, pathname, APP_ROUTES_PRE_FORM);
  if (appPreFormPath !== null) {
    const surface = surfaceForRoutedView(view, appPreFormPath, isLight);
    return surface ? Object.freeze({surface, routedPathname: appPreFormPath}) : null;
  }

  // This exact raw-path predicate is intentionally not derived from `view`.
  // The current App uses it as a host guard, so a transition-frame view cannot
  // move the form ahead of, or behind, a physical broiler pathname. Create mode
  // on /broiler/timeline (`!hasEditId`) yields to view.timeline so
  // BroilerTimelineView hosts the New Broiler Batch modal over the visible Gantt.
  const inBroilerSpace = pathname === '/broiler' || pathname.startsWith('/broiler/');
  const hasEditId = typeof editId === 'string' ? editId.trim().length > 0 : Boolean(editId);
  const isTimelineCreate = pathname === '/broiler/timeline' && !hasEditId;
  if (showForm && inBroilerSpace && !pathname.startsWith('/broiler/batches/') && !isTimelineCreate) {
    return Object.freeze({surface: 'broiler.batchForm.fullscreen', routedPathname: pathname});
  }

  for (const table of DOMAIN_DISPATCH_ORDER) {
    const routedPathname = routedPathnameFor(view, pathname, table);
    if (routedPathname === null) continue;
    const surface = surfaceForRoutedView(view, routedPathname, isLight);
    return surface ? Object.freeze({surface, routedPathname}) : null;
  }

  const appPostFormPath = routedPathnameFor(view, pathname, APP_ROUTES_POST_FORM);
  if (appPostFormPath !== null) {
    const surface = surfaceForRoutedView(view, appPostFormPath, isLight);
    return surface ? Object.freeze({surface, routedPathname: appPostFormPath}) : null;
  }

  return null;
}

function legacy(
  reason: AuthenticatedSurfaceFallback,
  surface: string | null = null,
  routedPathname: string | null = null,
): AuthenticatedSurfaceResolution {
  return Object.freeze({mode: AUTHENTICATED_SURFACE_MODE.LEGACY, reason, surface, routedPathname});
}

/**
 * Resolve the one authenticated render surface a current lease may claim. This
 * function owns no state, starts no request, and makes no release by itself.
 * The retained `legacy` enum label means "not released"; App converts those
 * results into synchronization, canonical redirect, or terminal recovery.
 */
export function resolveAuthenticatedSurface(input: AuthenticatedSurfaceResolverInput): AuthenticatedSurfaceResolution {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.MALFORMED_INPUT);
  }

  const pathname = pathnameFrom(input.location);
  if (!pathname || (input.showForm !== undefined && typeof input.showForm !== 'boolean')) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.MALFORMED_INPUT);
  }
  if (typeof input.view !== 'string' || !authenticatedViews.has(input.view)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNKNOWN_VIEW);
  }
  if (!validEpoch(input.sessionEpoch) || !validEpoch(input.leaseEpoch) || input.sessionEpoch !== input.leaseEpoch) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.INVALID_SESSION_EPOCH);
  }

  const identity = resolvedIdentity(input.authState);
  if (!identity) {
    // Distinguish the routine profile-pending state from an absent/invalid
    // identity without ever attempting the tolerant navigation path.
    const auth = input.authState as {user?: unknown; role?: unknown; profile?: unknown} | null;
    const hasIdentity =
      !!auth &&
      typeof auth === 'object' &&
      !!auth.user &&
      typeof auth.user === 'object' &&
      typeof (auth.user as {id?: unknown}).id === 'string' &&
      typeof auth.role === 'string';
    return legacy(
      hasIdentity ? AUTHENTICATED_SURFACE_FALLBACK.PROFILE_UNRESOLVED : AUTHENTICATED_SURFACE_FALLBACK.UNAUTHENTICATED,
    );
  }
  if (
    input.leaseUserId !== undefined &&
    (typeof input.leaseUserId !== 'string' || input.leaseUserId.trim() === '' || identity.userId !== input.leaseUserId)
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.SESSION_SUBJECT_MISMATCH);
  }
  // Migration 218: a deactivated account may render no released surface at
  // all, program or not -- Home, Tasks and the daily hub included. App shows it
  // InactiveAccountScreen instead, and refusing the claim here means no route
  // resource can be minted for it even if that render gate were bypassed.
  if (isInactiveAccount(identity.accessAuthState)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.ACCESS_DENIED);
  }

  const access = resolveSurfaceAccess({
    authState: identity.accessAuthState,
    view: input.view,
    viewToProgram: VIEW_TO_PROGRAM,
    isProcessingRole,
  });
  if (access.navigation !== SURFACE_NAVIGATION.ALLOW || !access.mayLoadProgramData) {
    return legacy(
      access.profileLoaded
        ? AUTHENTICATED_SURFACE_FALLBACK.ACCESS_DENIED
        : AUTHENTICATED_SURFACE_FALLBACK.PROFILE_UNRESOLVED,
    );
  }

  const candidate = resolveCandidate(input.view, pathname, input.showForm === true, input.editId, identity.isLight);
  if (!candidate) return legacy(AUTHENTICATED_SURFACE_FALLBACK.ROUTE_UNRESOLVED);
  if (!knownSurfaces.has(candidate.surface)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNKNOWN_SURFACE, candidate.surface, candidate.routedPathname);
  }

  const ledger = input.ledger === undefined ? BOOT_RELEASE_LEDGER : (input.ledger as BootReleaseLedger);
  const validation = validateBootReleaseLedger(ledger, knownSurfaces);
  if (!validation.valid) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.MALFORMED_RELEASE_LEDGER, candidate.surface, candidate.routedPathname);
  }
  // LayerBatchesRouter's wildcard row owns record descendants and the bare
  // housing base. The independent release audits only `/layer/batches`; every
  // other raw location must remain outside the released claim even when the
  // generic route classifier names the index surface.
  if (candidate.surface === 'layer.batches.index' && !isCanonicalLayerBatchesIndexLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // EquipmentHome owns a broad /fleet/* compatibility space. Its index release
  // admits exactly the two audited raw routes; trailing slashes, record paths,
  // entry namespaces, aliases, and transition-frame canonicalization must all
  // remain outside the released renderer rather than suppressing recovery.
  if (candidate.surface === 'equipment.fleet.index' && !isCanonicalFleetIndexLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // TasksRouter owns a broad /tasks/* compatibility space. Its Task Center
  // release admits exactly the four audited raw forms; a task record, a To Do
  // record, and transition-frame canonicalization onto /tasks must all remain
  // outside the released renderer rather than suppressing recovery.
  if (candidate.surface === 'tasks.center' && !isCanonicalTasksCenterLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // CattleBatchesRouter historically accepts a broad suffix space:
  // forecast projection paths, nested historic paths, and malformed record
  // suffixes must remain outside the released renderer. The independent release has
  // audited only one decoded-safe, queryless record id, so fence it before
  // ledger membership rather than letting this permanent surface suppress its
  // own recovery disposition.
  if (candidate.surface === 'cattle.batches.detail' && !isCanonicalCattleBatchesDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // CattleForecastBatchPage deliberately accepts a broad historical suffix
  // space and paints an invalid-month state itself. The independent release is
  // narrower: one raw decoded-safe suffix with no search/hash may bypass App
  // boot, including the page's intentional invalid-month state. Fence before
  // ledger membership so unsafe forecast paths cannot suppress their own
  // recovery disposition once this surface is listed.
  if (
    candidate.surface === 'cattle.batches.forecastDetail' &&
    !isCanonicalCattleForecastBatchDetailLocation(input.location)
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // SheepDailysRouter's record half. This fence shipped alone, while the pair
  // was still asymmetric — the record page released, its own hub not. The hub
  // now has its own fence further down, and the shapes this one refuses are
  // unchanged: every raw form the release did not audit — a nested path, an
  // encoded separator, a dot segment, a malformed escape, or a query/hash deep
  // link the page itself interprets — has to remain outside the released claim.
  // Fence it before ledger membership, or a RELEASED status here would suppress
  // terminal recovery and strand the page.
  if (candidate.surface === 'sheep.dailys.detail' && !isCanonicalSheepDailysDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // `/cattle/batches/*` is a SPLAT row, which makes this index the generic
  // answer for the whole cattlebatches view: `routedLocationFor` rewrites any
  // unowned pathname back to the bare base during a view-led transition frame,
  // and the empty-suffix trailing slash classifies here too. Neither is the
  // audited direct-entry route. Both clauses are load-bearing — the routed
  // pathname rejects `/cattle/batches/`, and the raw location rejects the
  // canonicalized transition frame — and both must run BEFORE ledger
  // membership, or a listed index surface would refuse its own release claim
  // while simultaneously suppressing the terminal recovery that keeps the
  // frame from rendering nothing.
  if (
    candidate.surface === 'cattle.batches.index' &&
    (candidate.routedPathname !== CATTLE_BATCHES_INDEX_PATHNAME ||
      !isCanonicalCattleBatchesIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (
    candidate.surface === 'cattle.dailys.index' &&
    (candidate.routedPathname !== CATTLE_DAILYS_INDEX_PATHNAME || !isCanonicalCattleDailysIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // EquipmentHome historically accepts a broad /fleet/* compatibility space.
  // Only its one decoded-safe, unreserved record segment was audited for a
  // released claim; aliases, entry namespaces, malformed suffixes, and every
  // nested path remain outside this renderer and recover explicitly.
  if (candidate.surface === 'equipment.fleet.detail' && !isCanonicalFleetDetailPathname(candidate.routedPathname)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // `/fleet/*` is a single splat row, so EquipmentHome classifies EVERY suffix
  // under the two entry prefixes as an entry surface — including the empty id
  // it renders as a fail-closed not-found, and every nested or encoded shape.
  // The independent releases audited exactly one decoded-safe record segment
  // each. Fence both before ledger membership: an unfenced entry release would
  // reject its own render claim at the strict boundary while simultaneously
  // suppressing terminal recovery, stranding the page.
  if (
    candidate.surface === 'equipment.fueling.entry' &&
    !isCanonicalFleetFuelingEntryPathname(candidate.routedPathname)
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (
    candidate.surface === 'equipment.checklist.entry' &&
    !isCanonicalFleetChecklistEntryPathname(candidate.routedPathname)
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // FuelingHub historically owns the broad `/equipment/*` family. The
  // independent form release has audited only one canonical, non-reserved
  // segment. Keep every wider or malformed suffix outside the release before
  // ledger membership; otherwise a listed form surface would reject its claim
  // while also suppressing explicit recovery.
  if (
    candidate.surface === 'equipment.fueling.form' &&
    !isCanonicalEquipmentFuelingFormPathname(candidate.routedPathname)
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // CattleDailysRouter intentionally owns every non-empty suffix so historical
  // links preserve their historical router behavior. The independent release,
  // however, has audited only one canonical record segment. Keep every
  // wider/malformed suffix outside the release before ledger membership;
  // otherwise a listed detail surface would reject the claim yet also suppress
  // explicit recovery.
  if (
    candidate.surface === 'cattle.dailys.detail' &&
    !isCanonicalCattleDailysDetailPathname(candidate.routedPathname)
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // CattleHerdsRouter owns the `/cattle/herds/*` wildcard, and its BASE
  // `/cattle/herds/` classifies as the index because the suffix is empty. The
  // independent release audited the bare hub path only, so fence the base and
  // every other non-canonical hub shape before ledger membership; otherwise a
  // RELEASED status would suppress recovery for a route no reviewer ever
  // admitted.
  if (
    candidate.surface === 'cattle.herds.index' &&
    (candidate.routedPathname !== CATTLE_HERDS_INDEX_PATHNAME || !isCanonicalCattleHerdsIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // The other half of the same partition: any non-empty suffix reaches the
  // animal record under its historical router, but only one decoded-safe,
  // queryless record id is released. Nested paths, encoded separators, dot
  // segments, and malformed escapes remain terminally fenced.
  if (candidate.surface === 'cattle.herds.detail' && !isCanonicalCattleHerdsDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // Both sheep routers own a wildcard suffix space, so the generic classifier
  // names the index surface for the router base and the detail surface for
  // every non-empty suffix — including trailing slashes, nested paths, and
  // encoded separators the independent release never audited. Fence all four
  // raw shapes here, before ledger membership, so a non-canonical location
  // keeps its recovery disposition instead of being stranded between two
  // boundaries.
  if (candidate.surface === 'sheep.batches.index' && !isCanonicalSheepBatchesIndexLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'sheep.batches.detail' && !isCanonicalSheepBatchesDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'sheep.flocks.index' && !isCanonicalSheepFlocksIndexLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'sheep.flocks.detail' && !isCanonicalSheepFlocksDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // `{view: 'tasks', paths: ['/tasks/*']}` is a SPLAT row, so `routedLocationFor`
  // hands back the raw pathname for every descendant of `/tasks` and
  // canonicalizes anything else onto `/tasks` during a view-led frame. Without
  // these two fences a listed record surface would answer RELEASED for a
  // nested, trailing-slash, encoded, query-bearing, or arbitrary-hash path
  // that the strict render boundary then refuses. Because a RELEASED result is
  // not a recovery disposition, the page would strand with nothing to render.
  // Fence before ledger membership so those shapes terminal-recover. Only
  // canonical `#comment-<id>` anchors remain audited record renders; the
  // predicates otherwise partition: `/tasks/todo/<id>` leaves `todo/<id>`
  // in the instance position, and `todo` is reserved besides.
  if (candidate.surface === 'tasks.todo.detail' && !isCanonicalTasksTodoDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'tasks.instance.detail' && !isCanonicalTasksInstanceDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // `{view: 'layerdailys', paths: ['/layer/dailys/*']}` is a SPLAT row, so this
  // pair splits the same way the Tasks records do. The index is the generic
  // answer for the whole `layerdailys` view — `routedLocationFor` rewrites any
  // unowned pathname back to the bare base during a view-led transition frame,
  // and the empty-suffix trailing slash classifies here too — while the record
  // surface answers for every non-empty suffix, including nested, encoded, and
  // query/hash-bearing shapes the release never audited. Both fences run BEFORE
  // ledger membership: an unfenced RELEASED verdict would suppress terminal
  // recovery and strand the frame with nothing to render at all.
  if (
    candidate.surface === 'layer.dailys.index' &&
    (candidate.routedPathname !== LAYER_DAILYS_INDEX_PATHNAME || !isCanonicalLayerDailysIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'layer.dailys.detail' && !isCanonicalLayerDailysDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // `/layer/housings/*` is the SECOND path of the layerbatches row, and the
  // classifier names this surface for every non-empty descendant — including
  // the trailing-slash base, which LayerBatchesRouter really does mount as an
  // empty-id "not found", plus nested, encoded, and query/hash-bearing shapes
  // the release never audited. The record page interprets `location.hash` for
  // its `#comment-<id>` deep links, so a hash-carrying load is a different
  // render than the audited one. Fence before ledger membership: a RELEASED
  // verdict would suppress terminal recovery, so an unfenced release would
  // strand those shapes with nothing to render at all. The batch half of this
  // same view is independently fenced and never reaches this branch.
  if (candidate.surface === 'layer.housings.detail' && !isCanonicalLayerHousingsDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // `/layer/batches/*` is the FIRST path of that same row, and the classifier
  // names this surface for every descendant of it — including the
  // trailing-slash base, which LayerBatchesRouter really does mount as an
  // empty-id "not found", plus nested, encoded, and query/hash-bearing shapes
  // the release never audited. The record page interprets `location.hash` for
  // its `#comment-<id>` deep links, so a hash-carrying load is a different
  // render than the audited one. Fence before ledger membership: a RELEASED
  // verdict would suppress terminal recovery, so an unfenced release would
  // strand those shapes with nothing to render at all. The bare
  // `/layer/batches` never
  // reaches this branch — it classifies as the released hub above, which has
  // its own equality fence.
  if (candidate.surface === 'layer.batches.detail' && !isCanonicalLayerBatchesDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // ==========================================================================
  // The four remaining daily routers. Each owns ONE splat row, so each needs
  // the same TWO fences the released Cattle and Layer Dailys pairs already
  // carry, and for the same measured reasons rather than by analogy:
  //
  //   * the index is the generic answer for its whole view, so
  //     `routedLocationFor` rewrites ANY unowned pathname back to the bare base
  //     during a view-led transition frame, and the empty-suffix trailing slash
  //     classifies here too. Both clauses are load-bearing: the routed pathname
  //     rejects `/x/dailys/`, the raw location rejects the canonicalized frame.
  //   * the record surface answers for every non-empty suffix, including
  //     nested, encoded, dot-segment, malformed-escape, and query/hash-bearing
  //     shapes the release never audited — and all four record pages READ
  //     `location.hash` for their `#comment-<id>` deep links, so a
  //     hash-carrying load is a different render than the audited one.
  //
  // All of them run BEFORE ledger membership: an unfenced RELEASED verdict
  // suppresses terminal recovery and strands the frame with nothing at all to
  // render. Measured against the real resolver over a synthetic ledger, an
  // unfenced index answers RELEASED for six of sixteen probed shapes and an
  // unfenced record page for ten of sixteen; the fenced live Layer Dailys
  // control answers for three and one respectively.
  // ==========================================================================
  if (
    candidate.surface === 'layer.eggDailys.index' &&
    (candidate.routedPathname !== EGG_DAILYS_INDEX_PATHNAME || !isCanonicalEggDailysIndexLocation(input.location))
  ) {
    // The raw-location clause is extra load-bearing here: `/layer/dailys/*` and
    // `/layer/eggs/*` are two splat rows in the SAME domain table, so a frame
    // carrying `view: 'eggdailys'` at `/layer/dailys` canonicalizes onto
    // `/layer/eggs` and would otherwise answer RELEASED as this surface. The
    // mirror case is already refused by the `layer.dailys.index` clause above.
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'layer.eggDailys.detail' && !isCanonicalEggDailysDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (
    candidate.surface === 'pig.dailys.index' &&
    (candidate.routedPathname !== PIG_DAILYS_INDEX_PATHNAME || !isCanonicalPigDailysIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'pig.dailys.detail' && !isCanonicalPigDailysDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // PigBatchesView is another two-surface splat presenter. Its index must be
  // the exact base, while the record half must be one decoded-safe id with
  // only its independently audited `?trip=<safe-id>` focus carrier and no
  // hash.
  if (
    candidate.surface === 'pig.batches.index' &&
    (candidate.routedPathname !== PIG_BATCHES_INDEX_PATHNAME || !isCanonicalPigBatchesIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'pig.batches.detail' && !isCanonicalPigBatchesDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // The sheep hub's own fence. Note the deliberate asymmetry with its record
  // sibling above: a record predicate needs no `routedPathname` guard, because
  // a transition frame canonicalizes onto the BASE and the base cannot satisfy
  // the record prefix. The index needs both clauses.
  if (
    candidate.surface === 'sheep.dailys.index' &&
    (candidate.routedPathname !== SHEEP_DAILYS_INDEX_PATHNAME || !isCanonicalSheepDailysIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (
    candidate.surface === 'broiler.dailys.index' &&
    (candidate.routedPathname !== BROILER_DAILYS_INDEX_PATHNAME ||
      !isCanonicalBroilerDailysIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'broiler.dailys.detail' && !isCanonicalBroilerDailysDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // /broiler/batches/* is one splat. The index and record releases admit only
  // their canonical raw forms; all other suffixes and query/hash record links
  // must remain outside the released claim before ledger membership can
  // suppress recovery.
  if (
    candidate.surface === 'broiler.batches.index' &&
    (candidate.routedPathname !== BROILER_BATCHES_INDEX_PATHNAME ||
      !isCanonicalBroilerBatchesIndexLocation(input.location))
  ) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'broiler.batches.detail' && !isCanonicalBroilerBatchesDetailLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // The FIRST `view.*` fence in this chain, and the widest in effect. Every
  // predecessor keys on a dotted surface id produced by a `VIEW_VARIANTS`
  // split; `weighinsessions` has NO variant entry, so `surfaceForRoutedView`
  // returns this one id for the ENTIRE `/weigh-in-sessions/*` splat space —
  // the bare base, the splat base, every malformed id, and every view-led
  // transition frame, which `routedLocationFor` canonicalizes onto the bare
  // `/weigh-in-sessions`. Measured: unfenced, the resolver answers RELEASED
  // for all eighteen adversarial shapes probed.
  //
  // And unlike every earlier fence, an unfenced miss here does not heal.
  // `/weigh-in-sessions` has no `VIEW_TO_PATH` entry, App's redirect effect
  // returns early for this exact view/prefix pair, and the page derives its id
  // by string replacement — so a RELEASED verdict on a non-canonical shape
  // suppresses recovery, renders a garbage id, and has no repair path: a blank
  // page that never repairs itself. ONE raw-location clause is the whole
  // partition here (see weighInSessionRecordPath.ts): one decoded-safe record
  // path with no search and an optional canonical `#comment-<id>` anchor;
  // routed === raw for every owned path, and raw is foreign for every
  // transition frame.
  if (candidate.surface === 'view.weighinsessions' && !isCanonicalWeighInSessionRecordLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // The SECOND `view.*` fence, and the only one whose splat space has another
  // view's EXACT row nested inside it: `/dailys/tasks` is view.tasksWebform.
  // `routedLocationFor` refuses that raw path for this view and canonicalizes
  // onto `/dailys`, so an unfenced release would answer RELEASED there,
  // suppress recovery, and render the hub while the URL says Tasks —
  // TasksWebform would never mount and nothing would repair it.
  //
  // view.tasksWebform is RELEASED now, and that makes this fence load-bearing
  // in a stronger way than when it was written: the nested pathname is no
  // longer merely a historical nested route the hub would pre-empt; it is a
  // released surface with its own renderer branch that the hub would DISPLACE.
  //
  // That refusal used to come from the residual list, not from a fence: while
  // view.webformhub was `partial` the resolver's residual clause below caught
  // this exact input. The list is empty now because the surface really does own
  // all of its reads, so the protection has to be stated rather than inherited.
  //
  // The predicate is a WHITELIST of the seven pathnames the presenter can
  // navigate to, which is why no reserved-segment table appears here — see
  // webformHubDailysPath.ts.
  if (candidate.surface === 'view.webformhub' && !isCanonicalWebformHubDailysLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  // The THIRD `view.*` fence, and the second whose view has no `VIEW_VARIANTS`
  // split: `{view: 'sows', paths: ['/pig/sows/*']}` is a SPLAT row, so
  // `surfaceForRoutedView` answers this one id for the ENTIRE `/pig/sows`
  // space — the bare list, the splat base, every malformed id, every nested
  // path, and every view-led transition frame, which `routedLocationFor`
  // canonicalizes onto the bare `/pig/sows`. Measured: unfenced, the resolver
  // answers RELEASED for all eighteen adversarial shapes probed, twelve of
  // them at an OFF-BASE `routedPathname`.
  //
  // Half of that space has no repair path. `src/App.tsx` sets `view === 'sows'`
  // for any `/pig/sows/` subpath, and its redirect effect returns EARLY for
  // exactly that pair — so a RELEASED verdict on a malformed descendant would
  // suppress recovery, the released renderer would recognise no such pair and
  // return null, and nothing would navigate away: a blank page that never
  // repairs itself.
  //
  // ONE raw-location clause is the whole partition here, for the same reason it
  // is for `view.weighinsessions` (see pigSowsPath.ts): routed === raw for
  // every owned path, and raw is foreign for every transition frame, so a
  // `routedPathname` clause would be unfalsifiable. The predicate admits the
  // surface's two real modes and nothing else — `/pig/sows` and one
  // decoded-safe record segment beneath it.
  if (candidate.surface === 'view.farrowing' && !isCanonicalPigFarrowingLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (candidate.surface === 'view.sows' && !isCanonicalPigSowsLocation(input.location)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (!validation.releasedSurfaces.has(candidate.surface)) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.UNRELEASED_SURFACE, candidate.surface, candidate.routedPathname);
  }
  if (SURFACE_MANIFEST[candidate.surface].residualLegacy.length > 0) {
    return legacy(AUTHENTICATED_SURFACE_FALLBACK.RESIDUAL_LEGACY, candidate.surface, candidate.routedPathname);
  }

  return Object.freeze({
    mode: AUTHENTICATED_SURFACE_MODE.RELEASED,
    reason: 'released',
    surface: candidate.surface,
    routedPathname: candidate.routedPathname,
  });
}

export default resolveAuthenticatedSurface;
